// P0-B Batch 2 · Payroll Approvals Queue.
//
// Lists PENDING PLAF adjustments (module=payroll_line_adjustment,
// approval_state=pending). Before an approver clicks Approve/Reject
// the "impact" panel must show — System Value, Proposed Final,
// Delta/variance %, effect on Gross/Deductions/Net Pay, Reason,
// Evidence, Initiator — per operator directive #6.
//
// Dual sign-off self-guard: if action.actor_user_id === current user,
// the Approve/Reject buttons render disabled with a tooltip.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowsClockwise, ShieldWarning, Check, X, ArrowSquareOut, Info } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signed = (n) => {
    const v = Number(n || 0);
    return `${v >= 0 ? "+" : ""}${v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const shortDT = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";

const STATUTORY_CODES = new Set(["TDS", "PF_EE", "ESI_EE", "PT", "LWF_EE"]);


function ImpactRow({ label, value, valueClass = "" }) {
    return (
        <div className="flex justify-between items-baseline py-1 border-b last:border-0">
            <span className="text-[11px] uppercase tracking-wider text-slate-500">{label}</span>
            <span className={`text-xs font-mono ${valueClass}`}>{value}</span>
        </div>
    );
}


function DecisionDialog({ open, action, decision, onClose, onConfirm, self }) {
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    useEffect(() => { setReason(""); }, [action?.id]);
    if (!action) return null;
    const ns = action.new_state || {};
    const os = action.old_state || {};
    const sys = Number(os.value || 0);
    const proposed = Number(ns.value || 0);
    const delta = proposed - sys;
    const deltaPct = sys > 0 ? (delta / sys) * 100 : 0;
    const componentCode = action.module_context?.component_code || "—";
    const isStatutory = STATUTORY_CODES.has(componentCode);

    const confirm = async () => {
        setSubmitting(true);
        try { await onConfirm(action, reason); onClose(); }
        catch { /* toast already surfaced */ }
        finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="approvals-decision-dialog">
                <DialogHeader>
                    <DialogTitle>{decision === "approved" ? "Approve" : "Reject"} adjustment</DialogTitle>
                    <DialogDescription>
                        <span className="font-mono text-xs">{componentCode}</span>
                        {isStatutory && (
                            <Badge className="ml-2 bg-red-100 text-red-800 text-[9px]">
                                <ShieldWarning size={9} className="inline mr-0.5" />Statutory
                            </Badge>
                        )}
                    </DialogDescription>
                </DialogHeader>
                <div className="text-xs space-y-3">
                    <div className="border rounded p-2 bg-slate-50">
                        <div className="uppercase text-[10px] tracking-wider text-slate-500 mb-1">Payroll Impact</div>
                        <ImpactRow label="System Calculated" value={inr(sys)} />
                        <ImpactRow label="Proposed Final" value={inr(proposed)} valueClass="text-emerald-900 font-semibold" />
                        <ImpactRow label="Δ Admin Adjustment" value={signed(delta)} valueClass="text-amber-900 font-semibold" />
                        <ImpactRow label="Variance %" value={`${deltaPct.toFixed(1)}%`} valueClass={Math.abs(deltaPct) > 25 ? "text-red-800 font-semibold" : ""} />
                    </div>
                    <div className="border rounded p-2 bg-white">
                        <ImpactRow label="Initiator" value={action.actor_email || "—"} />
                        <ImpactRow label="Submitted" value={shortDT(action.created_at)} />
                        <ImpactRow label="Reason" value={action.reason || "—"} />
                        <ImpactRow label="Evidence" value={action.evidence_object_id || "(none)"} valueClass="text-[10px]" />
                    </div>
                    {self && (
                        <Alert variant="destructive" data-testid="approvals-self-guard-alert">
                            <AlertDescription className="text-xs">
                                You initiated this action. A different authorised user must decide it (dual sign-off enforced).
                            </AlertDescription>
                        </Alert>
                    )}
                    <div>
                        <Label>Decision note ({decision === "rejected" ? "required" : "optional"})</Label>
                        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="approvals-decision-reason" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button
                        variant={decision === "approved" ? "default" : "destructive"}
                        disabled={submitting || self || (decision === "rejected" && !reason.trim())}
                        onClick={confirm}
                        data-testid={`approvals-confirm-${decision}`}
                    >
                        {submitting ? "Submitting…" : decision === "approved" ? "Approve" : "Reject"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


export default function PayrollApprovalsQueue() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [filter, setFilter] = useState("all");   // all | statutory | variance
    const [dialog, setDialog] = useState({ open: false, action: null, decision: null });

    const load = useCallback(async () => {
        setLoading(true); setErr("");
        try {
            const res = await api.get(`/administrative-action/log`, {
                params: {
                    module: "payroll_line_adjustment",
                    approval_state: "pending",
                    limit: 100,
                },
            });
            setRows(res.data?.items || []);
        } catch (ex) {
            setErr(formatApiError(ex));
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        if (filter === "all") return rows;
        if (filter === "statutory") {
            return rows.filter((r) => STATUTORY_CODES.has(r.module_context?.component_code || ""));
        }
        if (filter === "variance") {
            return rows.filter((r) => r.module_context?.variance_guardrail_breached);
        }
        return rows;
    }, [rows, filter]);

    const decide = async (action, reason) => {
        try {
            await api.post(`/payroll/adjustment/decide/${action.id}`, {
                decision: dialog.decision,
                reason: reason || null,
            });
            toast.success(dialog.decision === "approved" ? "Adjustment approved" : "Adjustment rejected");
            load();
        } catch (ex) {
            toast.error(formatApiError(ex));
            throw ex;
        }
    };

    return (
        <div className="p-4 max-w-6xl mx-auto" data-testid="payroll-approvals-queue">
            <PageHeader
                title="Payroll Adjustment Approvals"
                subtitle="Pending high-governance adjustments awaiting authorised approver"
                actions={(
                    <Button size="sm" variant="outline" onClick={load} data-testid="approvals-refresh">
                        <ArrowsClockwise size={14} className="mr-1" />Refresh
                    </Button>
                )}
            />

            <div className="flex gap-2 mb-4 mt-2" data-testid="approvals-filters">
                {["all", "statutory", "variance"].map((f) => (
                    <Button
                        key={f}
                        size="sm"
                        variant={filter === f ? "default" : "outline"}
                        onClick={() => setFilter(f)}
                        data-testid={`approvals-filter-${f}`}
                    >
                        {f === "all" ? "All Pending" : f === "statutory" ? "Statutory" : "Variance-Breach"}
                    </Button>
                ))}
            </div>

            {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}

            {!loading && filtered.length === 0 && (
                <div className="text-sm text-slate-500 italic p-6 text-center border rounded" data-testid="approvals-empty">
                    <Info size={16} className="inline mr-1" />No pending adjustments in this view.
                </div>
            )}

            <div className="space-y-2">
                {filtered.map((a) => {
                    const componentCode = a.module_context?.component_code || "—";
                    const isStatutory = STATUTORY_CODES.has(componentCode);
                    const varianceBreached = !!a.module_context?.variance_guardrail_breached;
                    const sys = Number(a.old_state?.value || 0);
                    const proposed = Number(a.new_state?.value || 0);
                    const delta = proposed - sys;
                    const self = a.actor_user_id === user?.id;
                    return (
                        <div key={a.id} className="border rounded p-3 bg-white" data-testid={`approvals-row-${a.id}`}>
                            <div className="flex items-baseline justify-between mb-2">
                                <div>
                                    <span className="font-mono text-sm">{componentCode}</span>
                                    {isStatutory && (
                                        <Badge className="ml-2 bg-red-100 text-red-800 text-[9px]"><ShieldWarning size={9} className="inline mr-0.5" />Statutory</Badge>
                                    )}
                                    {varianceBreached && (
                                        <Badge className="ml-1 bg-amber-100 text-amber-800 text-[9px]">Variance-Breach</Badge>
                                    )}
                                </div>
                                <div className="text-[10px] text-slate-500 font-mono">
                                    AAS {a.id}
                                </div>
                            </div>
                            <div className="grid grid-cols-4 gap-3 text-xs mb-2">
                                <div>
                                    <div className="uppercase text-[10px] text-slate-500">System</div>
                                    <div className="font-mono">{inr(sys)}</div>
                                </div>
                                <div>
                                    <div className="uppercase text-[10px] text-amber-700">Δ Admin</div>
                                    <div className="font-mono text-amber-900">{signed(delta)}</div>
                                </div>
                                <div>
                                    <div className="uppercase text-[10px] text-emerald-700">Proposed Final</div>
                                    <div className="font-mono font-semibold text-emerald-900">{inr(proposed)}</div>
                                </div>
                                <div>
                                    <div className="uppercase text-[10px] text-slate-500">Initiator</div>
                                    <div className="text-[11px]">{a.actor_email || "—"}</div>
                                    <div className="text-[10px] text-slate-500">{shortDT(a.created_at)}</div>
                                </div>
                            </div>
                            <div className="text-[11px] text-slate-600 mb-2">
                                <strong>Reason:</strong> {a.reason || "—"} {a.evidence_object_id && <span className="ml-2">· <ArrowSquareOut size={10} className="inline" /> Evidence: <span className="font-mono">{a.evidence_object_id}</span></span>}
                            </div>
                            <div className="flex justify-between items-center">
                                <Link
                                    to={`/admin/payroll/runs/${a.entity_composite_key?.run_id}/employee/${a.entity_composite_key?.employee_id}/ops`}
                                    className="text-xs text-blue-600 hover:underline"
                                    data-testid={`approvals-open-ops-${a.id}`}
                                >
                                    View operational console →
                                </Link>
                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={self}
                                        title={self ? "You initiated this — a different user must decide" : ""}
                                        onClick={() => setDialog({ open: true, action: a, decision: "rejected" })}
                                        data-testid={`approvals-reject-btn-${a.id}`}
                                    >
                                        <X size={12} className="mr-1" />Reject
                                    </Button>
                                    <Button
                                        size="sm"
                                        disabled={self}
                                        title={self ? "You initiated this — a different user must decide" : ""}
                                        onClick={() => setDialog({ open: true, action: a, decision: "approved" })}
                                        data-testid={`approvals-approve-btn-${a.id}`}
                                    >
                                        <Check size={12} className="mr-1" />Approve
                                    </Button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {loading && <div className="text-sm text-slate-500">Loading…</div>}

            <DecisionDialog
                open={dialog.open}
                action={dialog.action}
                decision={dialog.decision}
                self={dialog.action?.actor_user_id === user?.id}
                onClose={() => setDialog({ open: false, action: null, decision: null })}
                onConfirm={decide}
            />
        </div>
    );
}
